import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// icons
import { BiCalendarCheck } from 'react-icons/bi';
import { MdPlace } from 'react-icons/md';
// api
import { getTypeSchedules } from 'api/company';
import { getAllSchedules } from 'api/pathForHook/company';
// helpers
import { convertDateToDDMMYYYY } from 'helpers';
//custom-hooks
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import useHandleSearch from 'utils/customHooks/useHandleSearch';
// components
import Head from 'components/metatags_helmet/metatags';
import { InputCustom } from 'components/input';
import FilteredResults from 'components/userzone/FilteredResults';
import CardEvent from 'components/event/cardEvent';
import TitleWithLine from 'components/titleWithLine';
// styles
import './schedule.scss';

const Schedule = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get('tags');
  const [activities, setActivities] = useState([]);
  const [listTypeSchedules, setListTypeSchedules] = useState([])
  const [itemId, setItemId] = useState(null)
  const [isMobile, setIsMobile] = useState(false);
  const [filter, setFilter] = useState({
    search: '',
    type_schedule: ''
  });
  const [page, setPage] = useState(1);
  const [responseInfo, setResponseInfo] = useState({});
  const { t } = useTranslation(['schedule', 'libraries']);
  const { assignedLibrary } = useAssignedLibrary();
  const { fetchData, stateData, loading } = useHandleSearch()
  const title = t('activities');
  const description = t('foundallactivities');
  const isTagsUrl = location.search.includes('?tags=');

  useEffect(() => {
    const getListTypeSchedules = async () => {
      const { data, status } = await getTypeSchedules();
      if (status === 200 && data?.results?.length > 0) {
        setListTypeSchedules(data.results);
      } else {
        setListTypeSchedules([]);
      }
    };
    getListTypeSchedules();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 685px)');
    const handleMediaQueryChange = (e) => {
      setIsMobile(e.matches);
    }

    mediaQuery.addEventListener('change', handleMediaQueryChange);
    handleMediaQueryChange(mediaQuery);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, [])

  useEffect(() => {
    if (tag) {
      setItemId(tag); 
    }
  }, [tag]);

  const getScheduleItems = async (typeScheduleId) => {
    let payload;
    if (tag && filter.search === '') {
      payload = {
        tags: tag,
        actives: true
      };
    } else {
      payload = {
        page,
        page_size: 10,
        search: filter.search !== '' ? filter.search : '',
        type_schedule: typeScheduleId || filter.type_schedule, 
        actives: true,
        ordered_by: 'start',
      };
    }

    try {
      await fetchData(getAllSchedules(), payload);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setActivities(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setActivities(null);
      setResponseInfo({ errors });
    }
  }, [stateData]);

  useEffect(() => {
    getScheduleItems();
  }, [page, filter]);

  const handleTypeScheduleClick = (id) => {
    setItemId(id); 
    setFilter((prev) => ({
      ...prev,
      type_schedule: id,
    }));
    getScheduleItems(id);
  };

  const clearsearch = () => {
    setFilter({
      search: '',
      type_schedule: '',
    });
    setItemId(null);
    getScheduleItems();
  };

  const CardMain = ({ activity }) => {
    return (
      <>
        {
          isMobile ?
            <CardEvent event={activity} isMobile={isMobile} />
            :
            (<Row className="mx-0">
              <Col md={12} lg={12} sm={11} className="activity-main ">
                <div className="d-flex activity-main__content">
                  <Col>
                    <h6 className="orange text-uppercase mt-4 activity-main__content-name">
                      {activity?.type_schedule.name}
                    </h6>
                    <h2 className="mt-5 mb-3 openSans text-uppercase font-weight-bold activity-main__content-title">
                      <Link to={`/${assignedLibrary}/agenda/${activity?.id}`} className="text-decoration-none text-black">
                        {activity?.title}
                      </Link>
                    </h2>
                    <div className="d-flex mt-5 gap-2 align-items-center">
                      <BiCalendarCheck className="orange" size={30} />
                      <span className="ml-2 font-weight-bold mt-1 font-size-14">
                        {convertDateToDDMMYYYY(activity?.start_date)}
                      </span>
                      {activity.start_date !== activity.end_date && (
                        <span className="font-size-14 mt-1">-</span>
                      )}
                      <span className="font-weight-bold font-size-14 mt-1">
                        &nbsp;{activity.start_date !== activity.end_date && convertDateToDDMMYYYY(activity?.end_date)}
                      </span>
                    </div>
                    <div className="d-flex mt-2">
                      {activity?.place && <MdPlace className="orange" size={30} />}
                      <p className="ml-2 text-uppercase font-weight-bold mt-2 w-80 font-size-14">{activity?.place}</p>
                    </div>
                  </Col>
                  <Col className="activity-main__image">
                    <Link to={`/${assignedLibrary}/agenda/${activity?.id}`}>
                      <img src={`${activity.image == undefined ? '/defaultimage.webp' : activity?.image}`} alt={`${activity?.title || t('imageactivity')}`} />
                    </Link>
                  </Col>
                </div>
                <p className="mt-3 mb-4  activity-main__body" dangerouslySetInnerHTML={{ __html: activity?.body }} />
              </Col>
            </Row>
            )
        }
      </>
    )
  }

  const renderActivities = () => {
    return (
      <>
        {activities?.map((activity, index) => {
          return index === 0 && !isTagsUrl ? (
            <CardMain key={activity.id} activity={activity} />
          ) : (
            <Col md={4} lg={4} sm={12} key={activity.id} className="mt-4 center-responsive-card">
              <CardEvent event={activity} />
            </Col>
          );
        })}
      </>
    );
  }

  return (
    <Container className="justify-content-center d-flex mainDiv">
      <Head description={description} title={title} noIndex={true} />
      <Row className='container-row justify-content-center'>
        <Col lg={3} md={9} sm={10}>
          <TitleWithLine text={t('schedule')} />
          <div>
            <Col md={12} className="mb-3">
              <InputCustom
                type="text"
                placeholder={t('localevent')}
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              />
            </Col>
            <Col md={12}>
              <Button onClick={clearsearch} className="w-100 btn-clear">
                {t('clearsearch', { ns: 'libraries' })}
              </Button>
            </Col>
            <Col md={12} className="item-typeschedule">
              {listTypeSchedules.length > 0 &&
                listTypeSchedules.map((item) => (
                  <div
                    key={item.id}
                    className="text-decoration-none text-reset cursor-pointer"
                    onClick={() => handleTypeScheduleClick(item.id)}
                  >
                    <Col
                      md={9}
                      className={`${itemId === item.id ? 'active' : ''} item-typeschedule__content d-flex align-items-center`} >
                      <img className="mr-2 item-typeschedule__content-image" src={item.icon} alt={item.name} />
                      <p className="mb-1 item-typeschedule__content-paragraph">{item.name}</p>
                    </Col>
                  </div>
                ))
              }
            </Col>
          </div>
        </Col>
        <Col lg={9} className="mt-4 h-100">
          <FilteredResults
            data={activities?.length === 0}
            noResults={responseInfo?.noResults}
            isLoading={loading}
            page={page}
            changePage={setPage}
            totalPages={responseInfo?.totalPages}
          >
            {renderActivities()}
          </FilteredResults>
        </Col>
      </Row>
    </Container>
  );
};

export default Schedule;